import { Badge } from '@/components/ui/badge'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { cashRowLabel, cellLines, periodLabel, shortDate, signedAmount } from '@/lib/cash'
import { formatDecimal } from '@/lib/format'
import { messages } from '@/lib/messages'
import type { CashWeekly } from '@/services/http/cash'

const text = messages.cash.drill

export interface CashCell {
  row: string
  period: number
}

/** Hücrenin kaynak kalemleri (S1): toplamı hücredeki değerle aynıdır. */
export function CashDrillPanel({
  weekly,
  cell,
  onClose,
}: {
  weekly: CashWeekly
  cell: CashCell | null
  onClose: () => void
}) {
  const period = cell ? weekly.periods[cell.period] : undefined
  const lines = cell ? cellLines(weekly, cell.row, cell.period) : []
  const total = cell ? lines.reduce((sum, line) => sum + signedAmount(cell.row, line), 0) : 0
  const label = period ? periodLabel(period) : undefined

  return (
    <Sheet open={cell !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{cell ? cashRowLabel(cell.row) : ''}</SheetTitle>
          <SheetDescription>
            {label ? text.description(`${label.title} (${label.range})`) : ''}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          {lines.length === 0 ? (
            <p className="text-text-secondary text-sm">{text.empty}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-secondary border-b text-left text-xs">
                  <th className="py-2 pr-2 font-medium">{text.date}</th>
                  <th className="py-2 pr-2 font-medium">{text.item}</th>
                  <th className="py-2 text-right font-medium">{text.amount}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={`${line.row}|${line.ref}|${index}`} className="border-b last:border-0">
                    <td className="py-2 pr-2 align-top whitespace-nowrap tabular-nums">
                      {shortDate(line.date)}
                    </td>
                    <td className="py-2 pr-2 align-top">
                      <span className="block break-words">
                        {line.description || line.cari_code || line.ref}
                      </span>
                      <span className="text-text-secondary flex flex-wrap items-center gap-1.5 text-xs">
                        <Badge size="sm">{text.sources[line.source] ?? line.source}</Badge>
                        {line.cari_code && <span>{line.cari_code}</span>}
                        {line.instrument && <span>{line.instrument}</span>}
                        {cell && cell.row !== line.row && <span>{cashRowLabel(line.row)}</span>}
                      </span>
                    </td>
                    <td className="py-2 text-right align-top whitespace-nowrap tabular-nums">
                      {formatDecimal(cell ? signedAmount(cell.row, line) : line.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SheetBody>
        <SheetFooter className="flex-row items-center justify-between">
          <span className="text-text-secondary text-sm">{text.count(lines.length)}</span>
          <span className="text-sm font-semibold tabular-nums" data-testid="cash-drill-total">
            {text.total}: {formatDecimal(total)}
          </span>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
